import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Chip,
    CircularProgress,
    FormControlLabel,
    Grid,
    Link,
    Switch,
} from '@mui/material';
import Hidden from '../../../components/Hidden';
import {
    AppContext,
    SttLabelFee,
    SttFeeRow,
    SttList,
    SttLabelPeriod,
    SelectGroupPopup,
    SttButtonNew,
} from '../../../sporttia/all';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import { useToggle } from '../../../lib/hooks';
import SttGroupMemberDialog from '../../../sporttia/dialogs/groupMemberDialog/SttGroupMemberDialog';
import { PERMISSIONS } from '../../../lib/permissions';

type MshipData = {
    id: number;
    user: {
        id: number;
    };
};

type Props = {
    mship: MshipData;
};

type GroupData = {
    id: number;
    name: string;
    numActiveUsers: number;
    type: string;
    fee: number;
    period: string;
};

type GroupMshipData = {
    id: number;
    group: GroupData;
    benefactor?: {
        name: string;
    } | null;
    enrollment?: {
        price: number;
    } | null;
    ini: string;
    end: string;
    fees?: unknown[];
};

type GroupMshipsResponse = {
    rows?: GroupMshipData[];
};

export default function MshipGroups({ mship }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [groupMships, setGroupMships] = useState<GroupMshipsResponse>({});
    const [selectGroupOpen, setSelectGroupOpen] = useState<boolean>(false);
    const [idPayModal, setIdPayModal] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [active, toggleActive] = useToggle(() => {
        loadGroups();
    });
    const [selectedGroup, setSelectedGroup] = useState<
        GroupMshipData | Record<string, never>
    >({});

    const loadGroups = () => {
        setLoading(true);
        cxt?.api('GET', `/mships/${mship.id}/groups/mships`, {
            params: {
                page: 1,
                rows: 100,
                fetchFees: 1,
                onlyActive: active,
            },
            success: (r: any) => {
                setGroupMships(r);
                setLoading(false);
            },
            error: () => {
                setLoading(false);
            },
        });
    };

    const addUserToGroup = (group: GroupData) => {
        cxt?.api('POST', `/groups/${group.id}/users/${mship.user.id}`, {
            success: () => {
                loadGroups();
                cxt?.showMessage('S', t('Saved'));
                setSelectGroupOpen(false);
            },
        });
    };

    const renderCell = (ug: GroupMshipData) => (
        <>
            <Grid container spacing={3}>
                <Grid size={{ md: 4, sm: 12, xs: 12 }}>
                    <Link
                        onClick={() => setSelectedGroup(ug)}
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {ug.group.name}
                    </Link>

                    {ug.benefactor && (
                        <p>
                            <Chip
                                size="small"
                                label={`Titular: ${ug.benefactor?.name}`}
                            />
                        </p>
                    )}
                </Grid>
                <Hidden mdDown>
                    <Grid sx={{ textAlign: 'center' }} size={{ md: 1 }}>
                        {ug.group.numActiveUsers}
                    </Grid>
                </Hidden>
                <Hidden mdDown>
                    <Grid
                        sx={{ textAlign: 'center' }}
                        size={{ md: 2, sm: 12, xs: 12 }}
                    >
                        {t(`GT.${ug.group.type}`)}
                    </Grid>
                </Hidden>
                <Hidden mdDown>
                    <Grid size={{ md: 1, sm: 12, xs: 12 }}>
                        {ug.enrollment && ug.enrollment.price}
                    </Grid>
                </Hidden>
                <Hidden mdDown>
                    <Grid
                        sx={{ textAlign: 'center' }}
                        size={{ md: 2, sm: 12, xs: 12 }}
                    >
                        <SttLabelPeriod ini={ug.ini} end={ug.end} />
                    </Grid>
                </Hidden>
                <Hidden mdDown>
                    <Grid size={{ md: 2, sm: 12, xs: 12 }}>
                        <SttLabelFee
                            interval={ug.group.period}
                            price={ug.group.fee}
                        />
                    </Grid>
                </Hidden>
            </Grid>

            <Box mt={2}>
                {ug.fees && (
                    <SttFeeRow
                        fees={ug.fees as any}
                        onFeeClicked={(id: any) => setIdPayModal(id)}
                    />
                )}
            </Box>
        </>
    );

    /**
     * Init
     */

    useEffect(loadGroups, []);

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={active}
                                disabled={loading}
                                onChange={(ev: any) =>
                                    toggleActive(ev.target.checked)
                                }
                            />
                        }
                        label={t('OnlyActiveOnes')}
                    />
                    {loading && <CircularProgress size={20} />}
                </Box>
                <SttButtonNew
                    onClick={() => setSelectGroupOpen(true)}
                    permission={PERMISSIONS.GROUP.MODIFY}
                />
            </Box>

            <Hidden mdDown>
                <Grid container spacing={3} className="px-4 pb-2 font-medium">
                    <Grid size={{ md: 4 }}>{t('Name')}</Grid>
                    <Grid className="text-center" size={{ md: 1 }}>
                        {t('Members')}
                    </Grid>
                    <Grid className="text-center" size={{ md: 2 }}>
                        {t('Type')}
                    </Grid>
                    <Grid size={{ md: 1 }}>{t('Enrollment')}</Grid>
                    <Grid className="text-center" size={{ md: 2 }}>
                        {t('Period')}
                    </Grid>
                    <Grid size={{ md: 2 }}>{t('Fee')}</Grid>
                </Grid>
            </Hidden>

            <SttList
                data={
                    groupMships.rows &&
                    groupMships.rows.map((ug: any) => ({
                        caption: renderCell(ug),
                    }))
                }
            />

            {selectGroupOpen && (
                <SelectGroupPopup
                    open
                    types="NORMAL,ABONADOS,FAMILY"
                    onClose={() => setSelectGroupOpen(false)}
                    onSelect={(group: any) => addUserToGroup(group)}
                />
            )}

            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    onClose={() => setIdPayModal(null)}
                />
            )}

            {(selectedGroup as GroupMshipData).id && (
                <SttGroupMemberDialog
                    {...({
                        idGroupMember: (selectedGroup as GroupMshipData).id,
                        showHistory: true,
                        group: (selectedGroup as any).group,
                        onClose: () => setSelectedGroup({}),
                        onUpdateMember: () => {
                            setSelectedGroup({});
                            loadGroups();
                        },
                    } as any)}
                />
            )}
        </>
    );
}
